import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Stack,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  CheckCircle,
  Warning,
  Error,
  Info,
  Download,
  Share,
  Print,
  ArrowBack,
  LocationOn,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import { auditAPI, type AuditSession, type AuditAnalysis } from '../services/api';
import RadarChart from '../components/Charts/RadarChart';
import ExportButtons from '../components/ExportButtons';

const AuditAnalysisPage: React.FC = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId: string }>();
  const { user } = useAuth();
  const { selectedCompany } = useCompany();

  const [session, setSession] = useState<AuditSession | null>(null);
  const [analysis, setAnalysis] = useState<AuditAnalysis | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (sessionId) {
      loadAnalysisData();
    }
  }, [sessionId]);

  const loadAnalysisData = async () => {
    try {
      setIsLoading(true);
      setError('');

      // Load session details
      const sessionData = await auditAPI.getAuditSession(parseInt(sessionId!));
      setSession(sessionData);

      // Load analysis results
      try {
        const analysisData = await auditAPI.getAuditAnalysis(parseInt(sessionId!));
        setAnalysis(analysisData);
        setIsLoading(false);
      } catch (analysisError: any) {
        if (analysisError.response?.status === 404) {
          // Analysis doesn't exist, generate it automatically
          console.log('Analysis not found, generating new analysis...');
          setIsGenerating(true);
          setIsLoading(false);  // Stop main loading, show generating state instead
          try {
            await auditAPI.generateAnalysis(parseInt(sessionId!));
            // Wait a moment for generation to complete, then load the analysis
            setTimeout(async () => {
              try {
                const analysisData = await auditAPI.getAuditAnalysis(parseInt(sessionId!));
                setAnalysis(analysisData);
                setIsGenerating(false);
              } catch (retryError) {
                console.error('Error loading generated analysis:', retryError);
                setError('Analysis generation completed, but failed to load results. Please refresh the page.');
                setIsGenerating(false);
              }
            }, 2000);
          } catch (generateError) {
            console.error('Error generating analysis:', generateError);
            setError('Failed to generate review insights. Please try again.');
            setIsGenerating(false);
          }
        } else {
          console.error('Error loading analysis data:', analysisError);
          setError('Failed to load review insights. Please try again.');
          setIsLoading(false);
        }
      }
    } catch (error: any) {
      console.error('Error loading session data:', error);
      setError('Failed to load review session. Please try again.');
      setIsLoading(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Poor';
  };

  const getTrendIcon = (trend: string) => {
    switch (trend.toLowerCase()) {
      case 'up':
        return <TrendingUp color="success" />;
      case 'down':
        return <TrendingDown color="error" />;
      default:
        return <TrendingFlat color="action" />;
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'high':
        return <Error color="error" />;
      case 'medium':
        return <Warning color="warning" />;
      case 'low':
        return <Info color="info" />;
      default:
        return <Info color="action" />;
    }
  };

  const handleExportPDF = async () => {
    try {
      await auditAPI.exportAuditPDF(parseInt(sessionId!));
      // Handle download or show success message
    } catch (error: any) {
      console.error('Error exporting PDF:', error);
      setError('Failed to export PDF. Please try again.');
    }
  };

  const handleShareResults = async () => {
    try {
      const shareUrl = await auditAPI.shareAuditResults(parseInt(sessionId!));
      // Handle sharing (copy to clipboard, etc.)
      navigator.clipboard.writeText(shareUrl);
      // Show success message
    } catch (error: any) {
      console.error('Error sharing results:', error);
      setError('Failed to share results. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (isGenerating) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="400px" gap={2}>
          <CircularProgress />
          <Typography variant="h6" color="text.secondary">
            Generating review insights...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This may take a few moments
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!session || !analysis) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error">
          Review insights not found or not available.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Review Insights
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {selectedCompany?.name} 
              {session.site_name && (
                <>
                  {' • '}
                  <LocationOn sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                  {session.site_name}
                </>
              )}
              {' • '}Completed {new Date(session.end_date!).toLocaleDateString()}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <ExportButtons 
              sessionId={parseInt(sessionId!)} 
              variant="menu"
              size="medium"
            />
            <Button
              variant="outlined"
              startIcon={<Share />}
              onClick={handleShareResults}
            >
              Share
            </Button>
            <Button
              variant="outlined"
              startIcon={<Print />}
              onClick={() => window.print()}
            >
              Print
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Error Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Overall Score */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Overall Performance Score
          </Typography>
          <Box display="flex" alignItems="center" gap={3}>
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 4,
                borderColor: `${getScoreColor(analysis.overall_score)}.main`,
                backgroundColor: `${getScoreColor(analysis.overall_score)}.light`,
              }}
            >
              <Typography variant="h4" fontWeight="bold">
                {analysis.overall_score}%
              </Typography>
            </Box>
            <Box>
              <Typography variant="h6" color={`${getScoreColor(analysis.overall_score)}.main`}>
                {getScoreLabel(analysis.overall_score)}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {analysis.total_questions} questions completed
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {analysis.completion_time && typeof analysis.completion_time === 'object' 
                  ? `Completed ${analysis.completion_time.display}`
                  : analysis.completion_time 
                    ? `Completed in ${analysis.completion_time} minutes`
                    : 'Completion time not available'}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Historical Performance Radar Chart */}
      {analysis.historical_comparison && (
        <Box sx={{ mb: 4 }}>
          <RadarChart 
            data={analysis.historical_comparison} 
            title="Performance Trends Over Time"
            detailedResults={analysis.detailed_results}
          />
        </Box>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {/* Category Scores and Key Findings */}
        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {/* Category Scores */}
          <Box sx={{ flex: 1, minWidth: 300 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Category Performance
                </Typography>
                <List>
                  {analysis.category_scores?.map((category, index) => (
                    <ListItem key={index} divider={index < analysis.category_scores!.length - 1}>
                      <ListItemIcon>
                        {getTrendIcon(category.trend)}
                      </ListItemIcon>
                      <ListItemText
                        primary={category.name}
                        secondary={`${category.score}% • ${category.question_count} questions`}
                      />
                      <Chip
                        label={getScoreLabel(category.score)}
                        color={getScoreColor(category.score)}
                        size="small"
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Box>

          {/* Key Findings */}
          <Box sx={{ flex: 1, minWidth: 300 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Key Insights
                </Typography>
                <List>
                  {analysis.key_findings?.map((finding, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        {getPriorityIcon(finding.priority)}
                      </ListItemIcon>
                      <ListItemText
                        primary={finding.title}
                        secondary={finding.description}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Recommendations */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Recommendations
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              {analysis.recommendations?.map((recommendation, index) => (
                <Box key={index} sx={{ flex: '1 1 400px' }}>
                  <Paper sx={{ p: 2, border: 1, borderColor: 'grey.300' }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      {getPriorityIcon(recommendation.priority)}
                      <Typography variant="subtitle1" fontWeight="bold">
                        {recommendation.title}
                      </Typography>
                      <Chip
                        label={recommendation.priority}
                        color={
                          recommendation.priority.toLowerCase() === 'high' ? 'error' :
                          recommendation.priority.toLowerCase() === 'medium' ? 'warning' : 'info'
                        }
                        size="small"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {recommendation.description}
                    </Typography>
                    {recommendation.actions && (
                      <Box>
                        <Typography variant="body2" fontWeight="bold" mb={1}>
                          Suggested Actions:
                        </Typography>
                        <List dense>
                          {recommendation.actions.map((action, actionIndex) => (
                            <ListItem key={actionIndex} sx={{ py: 0 }}>
                              <ListItemIcon sx={{ minWidth: 24 }}>
                                <CheckCircle fontSize="small" color="action" />
                              </ListItemIcon>
                              <ListItemText
                                primary={action}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </Paper>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>

        {/* Detailed Results */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Detailed Results
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                    <th style={{ textAlign: 'left', padding: '12px', fontWeight: 'bold' }}>Question</th>
                    <th style={{ textAlign: 'center', padding: '12px', fontWeight: 'bold' }}>Answer</th>
                    <th style={{ textAlign: 'center', padding: '12px', fontWeight: 'bold' }}>Score</th>
                    <th style={{ textAlign: 'left', padding: '12px', fontWeight: 'bold' }}>Category</th>
                  </tr>
                </thead>
                <tbody>
                  {analysis.detailed_results?.map((result, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid #f5f5f5' }}>
                      <td style={{ padding: '12px' }}>
                        <Typography variant="body2">
                          {result.question_text}
                        </Typography>
                      </td>
                      <td style={{ textAlign: 'center', padding: '12px' }}>
                        <Chip
                          label={result.answer_text}
                          color={
                            result.answer_score >= 80 ? 'success' :
                            result.answer_score >= 60 ? 'warning' : 'error'
                          }
                          size="small"
                        />
                      </td>
                      <td style={{ textAlign: 'center', padding: '12px' }}>
                        <Typography variant="body2" fontWeight="bold">
                          {result.answer_score}%
                        </Typography>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <Typography variant="body2" color="text.secondary">
                          {result.category}
                        </Typography>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center' }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/audit-selection')}
        >
          Back to Review Selection
        </Button>
        <Button
          variant="contained"
          onClick={() => navigate(`/audit-session/${sessionId}`)}
        >
          Review Answers
        </Button>
      </Box>
    </Container>
  );
};

export default AuditAnalysisPage; 